import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BellRing, Plus, Trash2, Send, X, Mail, MessageSquare, CalendarClock, Home, Layers, Save, Users, ChevronLeft, ChevronRight, CalendarDays, List } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const STATUS = ["scheduled", "pending_approval", "approved", "sent", "paused"];
const LABEL = { scheduled: "Scheduled", pending_approval: "Needs approval", approved: "Approved", sent: "Sent", paused: "Paused" };
const COLOR = {
  scheduled: "bg-slate-700/60 text-slate-200", pending_approval: "bg-amber-500/15 text-amber-300",
  approved: "bg-cyan-500/15 text-cyan-300", sent: "bg-emerald-500/15 text-emerald-300",
  paused: "bg-slate-700/60 text-slate-400",
};
const day = (s) => (s ? new Date(s).toLocaleDateString() : "");
const emptyForm = { property_id: "", service_type: "general", title: "", interval_days: 180, next_due: "", channels: ["email"], message: "", auto_recur: true };
const emptyBulk = { service_type: "general", title: "", interval_days: 365, next_due: "", channels: ["email"], message: "", auto_recur: true, skip_existing: true };

function ReminderCalendar({ items, month, onPrev, onNext, onReschedule }) {
  const [dragOverDay, setDragOverDay] = useState(null);
  const y = month.getFullYear(), m = month.getMonth();
  const startDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const todayUTC = (() => { const n = new Date(); return Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()); })();
  const dueUTC = (r) => { const d = new Date(r.next_due); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };
  const isOverdue = (r) => r.next_due && dueUTC(r) < todayUTC && ["scheduled", "pending_approval"].includes(r.status);
  const byDay = {};
  items.forEach((r) => {
    if (!r.next_due) return;
    const d = new Date(r.next_due);
    if (d.getUTCFullYear() === y && d.getUTCMonth() === m) (byDay[d.getUTCDate()] = byDay[d.getUTCDate()] || []).push(r);
  });
  const overdue = items.filter(isOverdue);
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="space-y-3" data-testid="reminder-calendar">
      <div className="flex items-center justify-between">
        <button data-testid="cal-prev" onClick={onPrev} className="rounded-full border border-slate-700 p-1.5 text-slate-300 hover:border-amber-500"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display text-lg font-semibold text-white">{month.toLocaleString(undefined, { month: "long", year: "numeric" })}</div>
        <button data-testid="cal-next" onClick={onNext} className="rounded-full border border-slate-700 p-1.5 text-slate-300 hover:border-amber-500"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {overdue.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-600/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="cal-overdue-banner">
          <CalendarClock className="h-4 w-4" /> {overdue.length} overdue reminder{overdue.length > 1 ? "s" : ""} awaiting action.
        </div>
      )}
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {DOW.map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="min-h-[86px] rounded-lg" />;
          const list = byDay[d] || [];
          const isToday = Date.UTC(y, m, d) === todayUTC;
          return (
            <div key={d} data-testid={`cal-day-${d}`}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverDay !== d) setDragOverDay(d); }}
              onDragLeave={() => setDragOverDay((v) => (v === d ? null : v))}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); setDragOverDay(null); if (id && onReschedule) onReschedule(id, y, m, d); }}
              className={`min-h-[86px] rounded-lg border p-1 ${dragOverDay === d ? "border-cyan-400 bg-cyan-500/10" : isToday ? "border-amber-500/60 bg-amber-500/5" : "border-slate-800 bg-slate-900/40"}`}>
              <div className="text-[10px] text-slate-400">{d}</div>
              <div className="mt-0.5 space-y-0.5">
                {list.slice(0, 3).map((r) => {
                  const canDrag = ["scheduled", "pending_approval"].includes(r.status);
                  return (
                    <div key={r.id} draggable={canDrag} data-testid={`cal-chip-${r.id}`}
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", r.id); e.dataTransfer.effectAllowed = "move"; }}
                      title={`${r.title} - ${r.property_label || ""} (${r.customer_name || "customer"})${canDrag ? " · drag to reschedule" : ""}`}
                      className={`truncate rounded px-1 py-0.5 text-[9px] ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${isOverdue(r) ? "bg-rose-500/20 text-rose-300" : COLOR[r.status] || "bg-slate-700 text-slate-200"}`}>{r.title}</div>
                  );
                })}
                {list.length > 3 && <div className="text-[9px] text-slate-500">+{list.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PropertyReminders() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [props, setProps] = useState([]);
  const [custMap, setCustMap] = useState({});
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState(emptyBulk);
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [calItems, setCalItems] = useState([]);

  const loadCal = useCallback(async () => {
    try { const { data } = await api.get(`/property-reminders`); setCalItems(data.items || []); } catch { /* non-blocking */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try { const { data } = await api.get(`/reminder-templates`); setTemplates(data.items || []); } catch { /* non-blocking */ }
  }, []);

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get(`/property-reminders`, { params: filter ? { status: filter } : {} }); setData(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setData({ items: [], counts: {}, service_types: {} }); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { if (view === "calendar") loadCal(); }, [view, calMonth, loadCal]);
  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([api.get(`/properties`), api.get(`/customers`)]);
        setProps(p.data || []);
        setCustMap(Object.fromEntries((c.data || []).map((x) => [x.id, x])));
      } catch { /* non-blocking */ }
    })();
  }, []);

  const svc = data?.service_types || {};
  const create = async () => {
    if (!form.property_id) { toast.error("Pick a property"); return; }
    if (!form.next_due) { toast.error("Set the next due date"); return; }
    if (!form.channels.length) { toast.error("Pick at least one channel"); return; }
    try {
      await api.post(`/property-reminders`, { ...form, next_due: `${form.next_due}T00:00:00Z`, interval_days: Number(form.interval_days) || 180 });
      toast.success("Reminder scheduled"); setShowNew(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const approve = async (id) => {
    try { const { data } = await api.post(`/property-reminders/${id}/approve`); toast.success(`Sent (${data.send_result})`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const dismiss = async (id) => { try { await api.post(`/property-reminders/${id}/dismiss`); toast.success("Skipped"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const rescheduleTo = async (id, yy, mm, dd) => {
    try {
      const nd = new Date(Date.UTC(yy, mm, dd, 12, 0, 0)).toISOString();
      await api.patch(`/property-reminders/${id}`, { next_due: nd });
      toast.success("Reminder rescheduled");
      loadCal(); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/property-reminders/${id}`); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const toggleChannel = (ch) => setForm((f) => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter((x) => x !== ch) : [...f.channels, ch] }));
  const toggleBulkChannel = (ch) => setBulk((f) => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter((x) => x !== ch) : [...f.channels, ch] }));

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({ ...f, service_type: t.service_type || "general", title: t.title || "", interval_days: t.interval_days || 180, channels: t.channels?.length ? t.channels : ["email"], message: t.message || "" }));
    toast.success(`Loaded "${t.name}"`);
  };
  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name the template"); return; }
    try {
      await api.post(`/reminder-templates`, { name: tplName.trim(), service_type: form.service_type, title: form.title, interval_days: Number(form.interval_days) || 180, channels: form.channels, message: form.message });
      toast.success("Template saved"); setTplName(""); loadTemplates();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delTemplate = async (id) => { try { await api.delete(`/reminder-templates/${id}`); loadTemplates(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const bulkCreate = async () => {
    if (!bulk.next_due) { toast.error("Set the first due date"); return; }
    if (!bulk.channels.length) { toast.error("Pick at least one channel"); return; }
    try {
      const { data } = await api.post(`/property-reminders/bulk`, { ...bulk, next_due: `${bulk.next_due}T00:00:00Z`, interval_days: Number(bulk.interval_days) || 365 });
      toast.success(`Scheduled ${data.created} · skipped ${data.skipped} of ${data.total_properties}`);
      setShowBulk(false); setBulk(emptyBulk); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const counts = data?.counts || {};
  const inp = "rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow>Retention</Eyebrow>
          <h1 className="mt-1 font-display text-2xl font-bold text-white">Property health reminders</h1>
          <p className="mt-1 text-sm text-slate-400">Recurring service checks per property. Due reminders wait for your approval, then go out by email &amp; SMS.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="reminder-bulk-btn" onClick={() => setShowBulk((s) => !s)} className="inline-flex items-center gap-2 rounded-full border border-cyan-600/40 px-4 py-2 text-sm text-cyan-300 hover:border-cyan-400"><Users className="h-4 w-4" /> Remind all properties</button>
          <button data-testid="reminder-new-btn" onClick={() => setShowNew((s) => !s)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm"><Plus className="h-4 w-4" /> New reminder</button>
        </div>
      </div>

      {showBulk && (
        <div className="card-tac space-y-3 p-4" data-testid="reminder-bulk-form">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><Users className="h-4 w-4 text-cyan-400" /> Seed one reminder across every property</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select data-testid="bulk-service" value={bulk.service_type} onChange={(e) => setBulk({ ...bulk, service_type: e.target.value, title: "" })} className={inp}>
              {Object.entries(svc).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input data-testid="bulk-title" value={bulk.title} onChange={(e) => setBulk({ ...bulk, title: e.target.value })} placeholder={svc[bulk.service_type] || "Custom title (optional)"} className={inp} />
            <label className="flex items-center gap-2 text-sm text-slate-400">First due
              <input data-testid="bulk-next-due" type="date" value={bulk.next_due} onChange={(e) => setBulk({ ...bulk, next_due: e.target.value })} className={inp + " flex-1"} />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">Repeat every
              <input data-testid="bulk-interval" type="number" min="1" value={bulk.interval_days} onChange={(e) => setBulk({ ...bulk, interval_days: e.target.value })} className={inp + " w-24"} /> days
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs text-slate-500">Channels:</span>
            <label className="flex items-center gap-1.5 text-sm text-slate-300"><input data-testid="bulk-channel-email" type="checkbox" checked={bulk.channels.includes("email")} onChange={() => toggleBulkChannel("email")} /> <Mail className="h-3.5 w-3.5" /> Email</label>
            <label className="flex items-center gap-1.5 text-sm text-slate-300"><input data-testid="bulk-channel-sms" type="checkbox" checked={bulk.channels.includes("sms")} onChange={() => toggleBulkChannel("sms")} /> <MessageSquare className="h-3.5 w-3.5" /> SMS</label>
            <label className="flex items-center gap-1.5 text-sm text-slate-400"><input data-testid="bulk-skip-existing" type="checkbox" checked={bulk.skip_existing} onChange={(e) => setBulk({ ...bulk, skip_existing: e.target.checked })} /> Skip properties that already have this reminder</label>
          </div>
          <button data-testid="bulk-create" onClick={bulkCreate} className="btn-amber rounded-full px-5 py-2 text-sm">Schedule across all properties</button>
        </div>
      )}

      {counts.pending_approval > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300" data-testid="reminder-pending-banner">
          <BellRing className="h-4 w-4" /> {counts.pending_approval} reminder{counts.pending_approval > 1 ? "s" : ""} awaiting your approval to send.
        </div>
      )}

      {showNew && (
        <div className="card-tac space-y-3 p-4" data-testid="reminder-new-form">
          {templates.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-800/40 px-3 py-2" data-testid="reminder-template-bar">
              <Layers className="h-4 w-4 text-cyan-400" />
              <span className="text-xs text-slate-400">Load preset:</span>
              <select data-testid="reminder-template-select" defaultValue="" onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} className={inp + " py-1 text-xs"}>
                <option value="">Choose a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <div className="ml-auto flex flex-wrap gap-1">
                {templates.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400">
                    {t.name}
                    <button data-testid={`template-delete-${t.id}`} onClick={() => delTemplate(t.id)} className="text-rose-400 hover:text-rose-300"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <select data-testid="reminder-property" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} className={inp}>
              <option value="">Select property…</option>
              {props.map((p) => <option key={p.id} value={p.id}>{p.label} - {custMap[p.customer_id]?.name || "customer"}</option>)}
            </select>
            <select data-testid="reminder-service" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value, title: "" })} className={inp}>
              {Object.entries(svc).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input data-testid="reminder-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={svc[form.service_type] || "Custom title (optional)"} className={inp} />
            <label className="flex items-center gap-2 text-sm text-slate-400">First due
              <input data-testid="reminder-next-due" type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} className={inp + " flex-1"} />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">Repeat every
              <input data-testid="reminder-interval" type="number" min="1" value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} className={inp + " w-24"} /> days
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input data-testid="reminder-autorecur" type="checkbox" checked={form.auto_recur} onChange={(e) => setForm({ ...form, auto_recur: e.target.checked })} /> Auto-reschedule after each send
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs text-slate-500">Channels:</span>
            <label className="flex items-center gap-1.5 text-sm text-slate-300"><input data-testid="reminder-channel-email" type="checkbox" checked={form.channels.includes("email")} onChange={() => toggleChannel("email")} /> <Mail className="h-3.5 w-3.5" /> Email</label>
            <label className="flex items-center gap-1.5 text-sm text-slate-300"><input data-testid="reminder-channel-sms" type="checkbox" checked={form.channels.includes("sms")} onChange={() => toggleChannel("sms")} /> <MessageSquare className="h-3.5 w-3.5" /> SMS</label>
          </div>
          <textarea data-testid="reminder-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Custom message (optional - a friendly default is used if blank)" rows={2} className={inp + " w-full"} />
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3">
            <button data-testid="reminder-create" onClick={create} className="btn-amber rounded-full px-5 py-2 text-sm">Schedule reminder</button>
            <span className="text-slate-600">·</span>
            <input data-testid="reminder-tplname" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Save these settings as a template…" className={inp + " flex-1 min-w-[180px]"} />
            <button data-testid="reminder-savetpl" onClick={saveTemplate} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-cyan-400"><Save className="h-3.5 w-3.5" /> Save template</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {view === "list" && (
            <>
              <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs ${!filter ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`} data-testid="reminder-filter-all">All</button>
              {STATUS.map((s) => (
                <button key={s} data-testid={`reminder-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs ${filter === s ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`}>
                  {LABEL[s]} {counts[s] ? `(${counts[s]})` : ""}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="flex rounded-full border border-slate-700 p-0.5">
          <button data-testid="view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${view === "list" ? "bg-amber-500 text-black" : "text-slate-300"}`}><List className="h-3.5 w-3.5" /> List</button>
          <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${view === "calendar" ? "bg-amber-500 text-black" : "text-slate-300"}`}><CalendarDays className="h-3.5 w-3.5" /> Calendar</button>
        </div>
      </div>

      {view === "calendar" ? (
        <ReminderCalendar items={calItems} month={calMonth} onReschedule={rescheduleTo}
          onPrev={() => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          onNext={() => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))} />
      ) : data == null ? <div className="py-16"><Spinner label="Loading reminders" /></div>
        : data.items.length === 0 ? <div className="card-tac py-16 text-center text-slate-500" data-testid="reminders-empty">No reminders yet. Schedule recurring property health checks to stay top-of-mind with clients.</div>
          : (
            <div className="space-y-3">
              {data.items.map((r) => (
                <div key={r.id} data-testid={`reminder-${r.id}`} className={`card-tac p-4 ${r.status === "pending_approval" ? "border-amber-600/40" : ""}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">{r.title}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${COLOR[r.status] || "bg-slate-700 text-slate-200"}`}>{LABEL[r.status] || r.status}</span>
                        {(r.channels || []).map((c) => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">{c === "email" ? <Mail className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}{c}</span>)}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono-tac text-[10px] text-slate-500">
                        <span className="flex items-center gap-1"><Home className="h-3 w-3" />{r.property_label || "property"} · {r.customer_name || "customer"}</span>
                        <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />Due {day(r.next_due)}{r.auto_recur ? ` · every ${r.interval_days}d` : ""}</span>
                        {r.last_sent_at && <span className="text-emerald-400/70">Last sent {day(r.last_sent_at)}{r.send_result ? ` (${r.send_result})` : ""}</span>}
                        {!r.customer_email && (r.channels || []).includes("email") && <span className="text-rose-400/80">No customer email</span>}
                        {!r.customer_phone && (r.channels || []).includes("sms") && <span className="text-rose-400/80">No customer phone</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.status === "pending_approval" && (
                        <>
                          <button data-testid={`reminder-approve-${r.id}`} onClick={() => approve(r.id)} className="btn-amber inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs"><Send className="h-3.5 w-3.5" /> Approve &amp; send</button>
                          <button data-testid={`reminder-dismiss-${r.id}`} onClick={() => dismiss(r.id)} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500"><X className="h-3.5 w-3.5" /> Skip</button>
                        </>
                      )}
                      {r.status === "scheduled" && (
                        <button data-testid={`reminder-sendnow-${r.id}`} onClick={() => approve(r.id)} className="inline-flex items-center gap-1 rounded-full border border-cyan-600/40 px-3 py-1.5 text-xs text-cyan-300 hover:border-cyan-400"><Send className="h-3.5 w-3.5" /> Send now</button>
                      )}
                      <button data-testid={`reminder-delete-${r.id}`} onClick={() => del(r.id)} className="rounded-full border border-slate-700 p-1.5 text-rose-300 hover:border-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}
